import React from 'react';
import { useTranslation } from 'react-i18next';
import SearchableSelectField, { SearchableSelectOption } from '../ui/SearchableSelectField';
import { useCountryOptions } from '../../hooks/useCountryOptions';

interface Props {
    /** The stored value: the country's English common name ("Germany"). */
    value: string;
    /** Called with the English common name of the picked country. */
    onChange: (value: string) => void;
    label: string;
    disabled?: boolean;
}

/**
 * A booking contact's country, shown in the operator's language and stored as
 * the English common name every booking record carries (`useCountryOptions`).
 * The series screens had a free-text field here, so a German screen showed
 * "Germany" and a typed "Deutschland" would have been stored as such (staging
 * 2026-10-13).
 *
 * A stored value that is no country name (typed before this field existed)
 * stays shown as it is until someone picks a country.
 */
const BookingCountryField: React.FC<Props> = ({ value, onChange, label, disabled = false }) => {
    const { t } = useTranslation();
    const { options, toValue } = useCountryOptions();

    // A legacy value typed in the screen's language ("Deutschland") is that
    // country: shown as its option, stored as the English name on the next
    // pick. Anything else unrecognised stays shown as typed.
    const trimmed = value.trim();
    const shown = toValue(trimmed);
    const selectOptions = React.useMemo((): SearchableSelectOption[] => {
        const list = options.map((countryLabel) => ({ value: toValue(countryLabel), label: countryLabel }));
        return shown && !list.some((option) => option.value === shown)
            ? [{ value: shown, label: shown }, ...list]
            : list;
    }, [options, toValue, shown]);

    return (
        <div className={'flex flex-col gap-1'}>
            <label className={'text-sm font-medium text-foreground/80'}>{label}</label>
            <SearchableSelectField
                value={shown}
                onChange={onChange}
                options={selectOptions}
                placeholder={t('organization.fields.country_placeholder')}
                searchPlaceholder={t('organization.fields.country_search')}
                emptyMessage={t('organization.fields.country_empty')}
                disabled={disabled}
            />
        </div>
    );
};

export default BookingCountryField;
